/* eslint-disable react/no-this-in-sfc */
import { action, computed, observable, autorun, toJS } from 'mobx';
import {
    TBlock,
    TBlockDataForServer,
    TOptionableQuestion
} from '~/types/builder.types';
import type { RootStore } from './root.store';
import { createBlockInstance } from '../utils/block.utils';
import { ContainerableBlock } from '../models/blocks/containerable-block.model';
import { isObjectAnImage } from '~/utils/image.utils';
import { v4 as uuidv4 } from 'uuid';
import { ServiceItemBlockDataForServer } from '~/models/blocks/service-item-block.model';

export class ClipboardStore {
    rootStore: RootStore;

    @observable private rawCopiedBlock: any = null;
    @observable private rawAllBlocks: any[] = [];
    @observable recentlyCopiedBlock: boolean = false;
    @observable didPasteFromClipboard: boolean = false;
    @observable isFirstTimeCopyOpen: boolean = false;
    @observable isCurrentlyPastingBlock: boolean = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        const rawDataFromStorage = localStorage.getItem('CLIP');
        const jsonData = JSON.parse(rawDataFromStorage);
        this.setRawData(jsonData);
        autorun(() => {
            if (!this.isEmptyState) {
                const data = {
                    copiedBlock: this.rawCopiedBlock,
                    allBlocks: this.rawAllBlocks
                };
                localStorage.setItem('CLIP', JSON.stringify(data));
            }
        });
        window.onstorage = this.storageEventListener();
    }

    @action.bound
    setCopiedBlock(block: TBlock) {
        this.rawCopiedBlock = this.stripBlockToPastableRawData(block);
        let blocks = [block];
        if ((block as ContainerableBlock)?.children?.length > 0) {
            const descendants = (block as ContainerableBlock).allDescendants();
            blocks = blocks.concat(descendants);
        }
        this.setAllBlocks(blocks);
    }

    @action.bound
    setAllBlocks(blocks: TBlock[]) {
        this.rawAllBlocks = blocks.map(block =>
            this.stripBlockToPastableRawData(block)
        );
    }

    private stripBlockToPastableRawData(block: TBlock) {
        return this.stripNonPastableData(
            toJS(block.getDataForServer(), { recurseEverything: true })
        );
    }

    @action.bound
    setDidPasteFromClipboard(didPasteFromClipboard: boolean) {
        this.didPasteFromClipboard = didPasteFromClipboard;
    }

    @action.bound
    setIsCurrentlyPastingBlock(state: boolean) {
        this.isCurrentlyPastingBlock = state;
    }

    @action.bound
    setIsFirstTimeCopyOpen(isFirstTimeCopyOpen: boolean) {
        this.isFirstTimeCopyOpen = isFirstTimeCopyOpen;
    }

    @action.bound
    setRecentlyCopiedBlock(didRecentlyCopiedBlock: boolean) {
        if (didRecentlyCopiedBlock) {
            this.recentlyCopiedBlock = didRecentlyCopiedBlock;
            setTimeout(() => {
                this.recentlyCopiedBlock = false;
            }, 2000);
        }
        this.recentlyCopiedBlock = didRecentlyCopiedBlock;
    }

    @computed
    get copiedBlock() {
        return this.loadDataFromStorage()(this.rawCopiedBlock);
    }

    @computed
    get allBlocks() {
        return this.rawAllBlocks.map(this.loadDataFromStorage());
    }

    @computed
    get isEmptyState() {
        return !(this.rawCopiedBlock && this.rawAllBlocks);
    }

    private setRawData(data) {
        if (data) {
            const { copiedBlock: rawCopiedBlock, allBlocks: rawAllBlocks } =
                data;
            if (this.shouldUpdateStore(rawAllBlocks)) {
                this.rawAllBlocks = rawAllBlocks;
                this.rawCopiedBlock = rawCopiedBlock;
                this.setDidPasteFromClipboard(false);
            }
        }
    }

    private shouldUpdateStore(rawAllBlocks) {
        const currentBlockIds = new Set(
            this.rawAllBlocks.map(({ block_id }) => block_id)
        );
        const newBlockIds = new Set(
            rawAllBlocks.map(({ block_id }) => block_id)
        );

        const areSetsEqual =
            currentBlockIds.size === newBlockIds.size &&
            [...currentBlockIds].every(value => newBlockIds.has(value));

        return !areSetsEqual;
    }

    private storageEventListener() {
        return event => {
            if (event.key === 'CLIP') {
                const data = JSON.parse(event.newValue);
                this.setRawData(data);
            }
        };
    }

    private loadDataFromStorage() {
        return rawBlock => {
            if (rawBlock) {
                const {
                    builderUiStore: { selectedFlow: flow }
                } = this.rootStore;

                return createBlockInstance(
                    rawBlock,
                    flow.draftRevision,
                    this.rootStore
                );
            }
            return null;
        };
    }

    private stripNonPastableData(blockData: TBlockDataForServer) {
        if (blockData.type === 'contract_html_block') {
            blockData?.contract?.fields_metadata.forEach(field => {
                field.default_value = null;
            });
        }

        if (blockData.type === 'question_block') {
            const question = blockData?.question as TOptionableQuestion;
            question?.options?.forEach(option => (option.option_id = uuidv4()));
        }

        if (
            blockData.type === 'service_selection_item_block' ||
            blockData.type === 'invoice_service_item_block'
        ) {
            (blockData as ServiceItemBlockDataForServer).service_asset_enabled =
                false;
        }

        this.stripMongoIdsFromObjectDeep(blockData);

        return blockData;
    }

    private stripMongoIdsFromObjectDeep(item: any) {
        Object.keys(item).forEach(key => {
            if (key === '_id') {
                delete item._id;

                return;
            }

            if (
                (typeof item[key] === 'object' || Array.isArray(item)) &&
                item[key] !== null &&
                !isObjectAnImage(item[key]) // We depend on the image mongo id to reuse the image, this is the only time we don't want to strip it from the an object which is being copied.
            ) {
                this.stripMongoIdsFromObjectDeep(item[key]);
            }
        });
    }
}
